import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCircle, ChevronDown, Eye, ExternalLink,
  FileText, Loader2, X, Download, Plus, CloudUpload, Link2, Trash2,
} from "lucide-react";
import {
  fetchOriginalInvoiceFile, fetchStorageIntegrations, exportInvoiceToStorage,
  recheckInvoice, updateInvoiceFields, updateInvoiceLineFields,
  deleteInvoiceLine, createInvoiceLine, deleteInvoice, resolveDuplicate,
  type StorageIntegration, type InvoiceFileLink,
} from "../services/apiService";
import { useNavigate } from "react-router-dom";
import { Invoice } from "../types";

const NAVY = "#2a2420";
const ORANGE = "#607840";

// ── Types ─────────────────────────────────────────────────────────────────────

type LineCorrection = {
  productCode?: string;
  description?: string;
  quantity?: string | number;
  unit?: string;
  unitPrice?: string | number;
  totalPrice?: string | number;
};

type InvoiceCorrection = {
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalAmount?: string | number;
};

export type DeleteTarget =
  | { type: "invoice"; id: string; title: string; body: string }
  | { type: "line"; id: string; title: string; body: string };

export type LineForm = {
  productCode: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  totalPrice: string;
  category: string;
};

const PROVIDER_LABELS: Record<string, string> = {
  dropbox: "Dropbox",
  google_drive: "Google Drive",
  onedrive: "OneDrive",
};

function formatNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function displayCurrency(value: number | null | undefined) {
  return value == null ? "—" : `$${value.toLocaleString()}`;
}

// ── DeleteConfirmModal ────────────────────────────────────────────────────────

export function DeleteConfirmModal({
  target, isDeleting, error, showExternalStorageWarning = false, onCancel, onDelete,
}: {
  target: DeleteTarget;
  isDeleting: boolean;
  error: string;
  showExternalStorageWarning?: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.48)" }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-lg p-5"
        style={{ backgroundColor: "white", boxShadow: "0 18px 50px rgba(0,0,0,0.24)", border: "1px solid rgba(220,38,38,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(220,38,38,0.08)" }}>
            <AlertTriangle size={17} style={{ color: "#dc2626" }} />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>{target.title}</h2>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: NAVY, opacity: 0.62 }}>{target.body}</p>
            <p className="text-xs mt-2 font-semibold text-red-600">This action can't be undone.</p>
            {showExternalStorageWarning && (
              <p className="text-xs mt-2 font-semibold text-red-600">
                This will also delete exported copies of this invoice from connected external storage.
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className="text-xs mt-4 rounded-md px-3 py-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-md px-4 py-2 text-xs font-bold transition-all disabled:opacity-50"
            style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: "#dc2626", boxShadow: "0 2px 8px rgba(220,38,38,0.25)" }}
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FormInput ─────────────────────────────────────────────────────────────────

export function FormInput({
  label, value, onChange, type = "text", required = false, placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number" | "date" | "email";
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>
        {label}{required ? " *" : ""}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md px-3 py-2 text-xs outline-none transition-all"
        style={{ border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: "white" }}
      />
    </label>
  );
}

// ── CategoryDropdown ──────────────────────────────────────────────────────────

export function CategoryDropdown({
  value, onChange, categories,
}: {
  value: string;
  onChange: (value: string) => void;
  categories: string[];
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>
        Category
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md px-3 py-2 text-xs outline-none transition-all"
        style={{ border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: "white" }}
      >
        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>
  );
}

// ── AddLineForm ───────────────────────────────────────────────────────────────

export function AddLineForm({
  defaultCategory, isSaving, onCancel, onSubmit, categories,
}: {
  defaultCategory: string;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (form: LineForm) => void;
  categories: string[];
}) {
  const [form, setForm] = useState<LineForm>({
    productCode: "", description: "", quantity: "", unit: "",
    unitPrice: "", totalPrice: "", category: defaultCategory || "Other",
  });

  function update<K extends keyof LineForm>(key: K, value: LineForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="rounded-lg p-4 mt-3" style={{ backgroundColor: "white", border: `1px solid ${NAVY}12` }}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: NAVY, opacity: 0.45 }}>
        Add invoice line
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <FormInput label="Product code" value={form.productCode} onChange={(v) => update("productCode", v)} />
        <FormInput label="Description" required value={form.description} onChange={(v) => update("description", v)} />
        <FormInput label="Qty" type="number" value={form.quantity} onChange={(v) => update("quantity", v)} />
        <FormInput label="Unit" value={form.unit} onChange={(v) => update("unit", v)} />
        <FormInput label="Unit price" type="number" value={form.unitPrice} onChange={(v) => update("unitPrice", v)} />
        <FormInput label="Total" type="number" value={form.totalPrice} onChange={(v) => update("totalPrice", v)} />
        <CategoryDropdown value={form.category} onChange={(v) => update("category", v)} categories={categories} />
        <div className="flex items-end justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="rounded-md px-3 py-2 text-xs font-bold disabled:opacity-50"
            style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(form)}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: ORANGE, boxShadow: "0 2px 8px rgba(96,120,64,0.25)" }}
          >
            {isSaving && <Loader2 size={13} className="animate-spin" />}
            Add line
          </button>
        </div>
      </div>
    </div>
  );
}

// ── OriginalFilePreviewModal ──────────────────────────────────────────────────

export function OriginalFilePreviewModal({ file, onClose, panelMode = false }: { file: InvoiceFileLink; onClose: () => void; panelMode?: boolean }) {
  const isImage = file.mimeType.startsWith("image/");
  const isPdf = file.mimeType === "application/pdf";
  const previewUrl = file.previewUrl || file.signedUrl;
  const downloadUrl = file.downloadUrl || file.signedUrl;

  return (
    <div
      className="fixed z-[60] flex"
      style={{
        ...(panelMode
          ? { top: 0, left: 0, bottom: 0, right: "55%", flexDirection: "column" }
          : { inset: 0, alignItems: "center", justifyContent: "center", padding: "1rem", backgroundColor: "rgba(15,23,42,0.62)" }),
      }}
      onClick={panelMode ? undefined : onClose}
    >
      <div
        className={panelMode ? "w-full h-full flex flex-col overflow-hidden" : "w-full max-w-5xl h-[86vh] rounded-lg overflow-hidden flex flex-col"}
        style={{ backgroundColor: "#fff", boxShadow: panelMode ? "4px 0 24px rgba(0,0,0,0.18)" : "0 20px 60px rgba(0,0,0,0.28)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${NAVY}1a` }}>
          <div className="min-w-0">
            <p className="text-sm font-bold truncate" style={{ color: NAVY }}>{file.fileName}</p>
            <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.45 }}>
              Private preview · expires in {Math.round(file.expiresInSeconds / 60)} min
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={downloadUrl} className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold text-white" style={{ backgroundColor: ORANGE }}>
              <Download size={13} />
              Download
            </a>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center" style={{ color: NAVY, backgroundColor: `${NAVY}0d` }}>
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0" style={{ backgroundColor: "#fff" }}>
          {isImage ? (
            <div className="w-full h-full flex items-center justify-center p-4">
              <img src={previewUrl} alt={file.fileName} className="max-w-full max-h-full object-contain rounded-md" />
            </div>
          ) : isPdf ? (
            <iframe title={file.fileName} src={previewUrl} className="w-full h-full border-0" />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <FileText size={34} style={{ color: NAVY, opacity: 0.28 }} />
              <p className="text-sm font-semibold mt-3" style={{ color: NAVY }}>Preview not available for this file type.</p>
              <a href={downloadUrl} target="_blank" rel="noreferrer" className="mt-4 px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: ORANGE }}>
                Open file
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── StorageExportModal ────────────────────────────────────────────────────────

export function StorageExportModal({ invoiceId, businessId, onClose }: { invoiceId: string; businessId?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exportResult, setExportResult] = useState<{ provider: string; status: string; path?: string; error?: string }[] | null>(null);
  const [exportError, setExportError] = useState("");

  const { data: integrations, isLoading } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", businessId],
    queryFn: () => fetchStorageIntegrations(businessId),
  });

  const connected: StorageIntegration[] = (integrations ?? []).filter((i: StorageIntegration) => i.status === "connected" && i.provider !== "onedrive");

  const exportMutation = useMutation({
    mutationFn: () => exportInvoiceToStorage(invoiceId, Array.from(selected), businessId),
    onSuccess: (result) => setExportResult(result),
    onError: (e) => setExportError(e instanceof Error ? e.message : "Export failed"),
  });

  function toggleProvider(id: string) {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  const allSelected = connected.length > 0 && selected.size === connected.length;
  function toggleAll() {
    if (allSelected) setSelected(new Set()); else setSelected(new Set(connected.map((i) => i.provider)));
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(15,23,42,0.48)" }} onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl p-5 space-y-4" style={{ backgroundColor: "white", boxShadow: "0 18px 50px rgba(0,0,0,0.22)", border: `1px solid ${NAVY}18` }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${NAVY}0d` }}>
              <CloudUpload size={15} style={{ color: NAVY }} />
            </div>
            <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>Upload to cloud</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md" style={{ color: NAVY, opacity: 0.4 }}><X size={15} /></button>
        </div>

        {exportResult ? (
          <div className="space-y-2">
            {exportResult.map((d) => (
              <div key={d.provider} className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{ backgroundColor: d.status === "success" ? "rgba(16,185,129,0.07)" : "rgba(220,38,38,0.06)", border: d.status === "success" ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(220,38,38,0.18)" }}>
                {d.status === "success"
                  ? <CheckCircle size={14} style={{ color: "#059669", flexShrink: 0, marginTop: 1 }} />
                  : <AlertTriangle size={14} style={{ color: "#dc2626", flexShrink: 0, marginTop: 1 }} />}
                <div className="min-w-0">
                  <p className="text-xs font-bold" style={{ color: d.status === "success" ? "#065f46" : "#991b1b" }}>{PROVIDER_LABELS[d.provider] ?? d.provider}</p>
                  <p className="text-[11px] mt-0.5 break-all" style={{ color: d.status === "success" ? "#047857" : "#b91c1c", opacity: 0.8 }}>{d.status === "success" ? (d.path || "Uploaded") : d.error}</p>
                </div>
              </div>
            ))}
            <button type="button" onClick={onClose} className="w-full rounded-lg py-2 text-xs font-bold mt-1" style={{ backgroundColor: `${NAVY}0d`, color: NAVY, border: `1px solid ${NAVY}1a` }}>Done</button>
          </div>
        ) : (
          <>
            {isLoading ? (
              <div className="flex items-center gap-2 py-3 justify-center">
                <Loader2 size={15} className="animate-spin" style={{ color: NAVY, opacity: 0.4 }} />
                <span className="text-xs" style={{ color: NAVY, opacity: 0.45 }}>Loading connections…</span>
              </div>
            ) : connected.length === 0 ? (
              <p className="text-xs text-center py-2" style={{ color: NAVY, opacity: 0.55 }}>No storage connections yet.</p>
            ) : (
              <div className="space-y-2">
                {connected.length > 1 && (
                  <button type="button" onClick={toggleAll} className="text-[11px] font-semibold" style={{ color: ORANGE }}>
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                )}
                {connected.map((integration) => {
                  const checked = selected.has(integration.provider);
                  return (
                    <label key={integration.provider} className="flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-all"
                      style={{ border: `1.5px solid ${checked ? `${NAVY}40` : `${NAVY}18`}`, backgroundColor: checked ? `${NAVY}08` : "white" }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProvider(integration.provider)} className="rounded" style={{ accentColor: ORANGE }} />
                      <span className="text-xs font-semibold" style={{ color: NAVY }}>{PROVIDER_LABELS[integration.provider] ?? integration.provider}</span>
                      {integration.rootFolderName && (
                        <span className="ml-auto text-[11px] truncate max-w-[100px]" style={{ color: NAVY, opacity: 0.45 }}>{integration.rootFolderName}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            {exportError && <p className="text-xs rounded-md px-3 py-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>{exportError}</p>}
            <div className="space-y-2 pt-1">
              <button type="button" onClick={() => exportMutation.mutate()} disabled={selected.size === 0 || exportMutation.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold text-white transition-all disabled:opacity-40"
                style={{ backgroundColor: ORANGE, boxShadow: selected.size > 0 ? "0 2px 8px rgba(96,120,64,0.28)" : undefined }}>
                {exportMutation.isPending && <Loader2 size={13} className="animate-spin" />}
                {exportMutation.isPending ? "Uploading…" : `Upload to ${selected.size > 0 ? `${selected.size} destination${selected.size > 1 ? "s" : ""}` : "…"}`}
              </button>
              <button type="button" onClick={() => navigate("/integrations")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold transition-all"
                style={{ color: NAVY, opacity: 0.6, backgroundColor: `${NAVY}07`, border: `1px solid ${NAVY}14` }}>
                <Link2 size={12} />
                Add connection
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── EditableCell ──────────────────────────────────────────────────────────────

export function EditableCell({
  value, type = "text", align = "left", onSave,
}: {
  value: string | number | null | undefined;
  type?: "text" | "number" | "date";
  align?: "left" | "right";
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(value == null ? "" : String(value));
  }, [value]);

  function save() {
    const next = draft.trim();
    if (next !== (value == null ? "" : String(value))) onSave(next);
  }

  return (
    <input
      type={type}
      value={draft}
      placeholder="null"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => { focusedRef.current = false; save(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(value == null ? "" : String(value)); e.currentTarget.blur(); }
      }}
      className={`w-full rounded-md px-2 py-1.5 text-xs outline-none transition-all ${align === "right" ? "text-right" : "text-left"}`}
      style={{ border: `1.5px solid ${NAVY}26`, color: NAVY, backgroundColor: "white" }}
    />
  );
}

// ── InvoiceDetailPanel ────────────────────────────────────────────────────────

export function InvoiceDetailPanel({
  inv,
  businessId,
  categories,
  onInvoiceDeleted,
  highlightProductCode,
  lineItemsOnly = false,
}: {
  inv: Invoice;
  businessId?: string;
  categories: string[];
  onInvoiceDeleted?: () => void;
  highlightProductCode?: string;
  lineItemsOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [recheckMessage, setRecheckMessage] = useState("");
  const [previewFile, setPreviewFile] = useState<InvoiceFileLink | null>(null);
  const [originalFile, setOriginalFile] = useState<InvoiceFileLink | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalMenuOpen, setExternalMenuOpen] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [externalStorageMessage, setExternalStorageMessage] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [addingLine, setAddingLine] = useState(false);
  const [lineCreateError, setLineCreateError] = useState("");

  const unresolvedAlarms = inv.alarms.filter((a) => !a.resolvedAt);
  const criticalAlarms = unresolvedAlarms.filter((a) => a.level === "critical");
  const warningAlarms = unresolvedAlarms.filter((a) => a.level !== "critical");
  const isFlagged = criticalAlarms.length > 0;
  const hasWarnings = warningAlarms.length > 0;
  const suggestedTotal = inv.lineItems.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  const suggestedDate = inv.uploadedAt ? inv.uploadedAt.slice(0, 10) : null;
  const externalDestinations = originalFile?.externalStorage || [];
  const externalStorageUnavailable = Boolean(originalFile) && externalDestinations.length === 0;
  const externalStorageNotice = externalStorageMessage || (externalStorageUnavailable ? "This invoice has not been exported to external storage." : "");
  const { data: storageIntegrationsData } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", businessId],
    queryFn: () => fetchStorageIntegrations(businessId),
    enabled: Boolean(businessId),
  });
  const storageIntegrations: StorageIntegration[] = storageIntegrationsData ?? [];
  const deletesExternalOnInvoiceDelete = storageIntegrations.some((integration) =>
    integration.status === "connected" && integration.deleteOnInvoiceDelete
  );

  useEffect(() => {
    setPreviewFile(null);
    setOriginalFile(null);
    setPreviewError("");
    setExternalStorageMessage("");
    setExternalMenuOpen(false);
  }, [inv.id, businessId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["invoices", businessId] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-summary", businessId] });
  };

  const invoiceMutation = useMutation({
    mutationFn: (updates: InvoiceCorrection) => updateInvoiceFields(inv.id, updates, businessId),
    onSuccess: invalidate,
  });

  const lineMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: LineCorrection }) => updateInvoiceLineFields(id, updates, businessId),
    onSuccess: invalidate,
  });

  const recheckMutation = useMutation({
    mutationFn: () => recheckInvoice(inv.id, businessId),
    onSuccess: (result) => {
      const warnings = result.warnings || [];
      setRecheckMessage(result.success
        ? warnings.length > 0 ? `Complete. Warnings: ${warnings.join(" ")}` : "Invoice is complete."
        : `Still missing: ${(result.missing || []).join(" ")}`);
      invalidate();
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: () => deleteInvoice(inv.id, businessId),
    onSuccess: () => { setDeleteTarget(null); invalidate(); onInvoiceDeleted?.(); },
    onError: (e) => setDeleteError(e instanceof Error ? e.message : "Could not delete invoice."),
  });

  const resolveDuplicateMutation = useMutation({
    mutationFn: (action: "delete" | "overwrite") => resolveDuplicate(inv.id, action, businessId),
    onSuccess: invalidate,
  });

  const deleteLineMutation = useMutation({
    mutationFn: (id: string) => deleteInvoiceLine(id, businessId),
    onSuccess: () => { setDeleteTarget(null); invalidate(); },
    onError: (e) => setDeleteError(e instanceof Error ? e.message : "Could not delete line."),
  });

  const createLineMutation = useMutation({
    mutationFn: (form: LineForm) => createInvoiceLine({
      invoiceId: inv.id,
      productCode: form.productCode,
      description: form.description,
      quantity: form.quantity,
      unit: form.unit,
      unitPrice: form.unitPrice,
      totalPrice: form.totalPrice,
      category: form.category,
    }, businessId),
    onSuccess: () => { setAddingLine(false); setLineCreateError(""); invalidate(); },
    onError: (e) => setLineCreateError(e instanceof Error ? e.message : "Could not add line."),
  });

  function confirmDelete(target: DeleteTarget) { setDeleteError(""); setDeleteTarget(target); }
  function handleDeleteConfirmed() {
    if (!deleteTarget) return;
    if (deleteTarget.type === "invoice") deleteInvoiceMutation.mutate();
    else deleteLineMutation.mutate(deleteTarget.id);
  }
  function handleAddLine(form: LineForm) {
    if (!form.description.trim()) { setLineCreateError("Description is required."); return; }
    setLineCreateError("");
    createLineMutation.mutate(form);
  }

  async function loadOriginalFile() {
    if (originalFile) return originalFile;
    const file = await fetchOriginalInvoiceFile(inv.id, businessId);
    setOriginalFile(file);
    return file;
  }

  async function openOriginalFile() {
    setPreviewError("");
    setPreviewLoading(true);
    try { setPreviewFile(await loadOriginalFile()); }
    catch (e) { setPreviewError(e instanceof Error ? e.message : "Could not load file."); }
    finally { setPreviewLoading(false); }
  }

  async function openExternalStorage() {
    setExternalStorageMessage("");
    setExternalLoading(true);
    try {
      const file = await loadOriginalFile();
      const destinations = file.externalStorage || [];
      if (destinations.length === 0) {
        setExternalStorageMessage("This invoice has not been exported to external storage.");
        return;
      }
      if (destinations.length === 1) {
        if (!destinations[0].url) {
          setExternalStorageMessage(`External path: ${destinations[0].path}`);
          return;
        }
        window.open(destinations[0].url, "_blank", "noopener,noreferrer");
        return;
      }
      setExternalMenuOpen((o) => !o);
    } catch (e) {
      setExternalStorageMessage(e instanceof Error ? e.message : "Could not load external storage links.");
    } finally { setExternalLoading(false); }
  }

  return (
    <>
      {previewFile && <OriginalFilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} panelMode />}
      {deleteTarget && (
        <DeleteConfirmModal
          target={deleteTarget}
          isDeleting={deleteInvoiceMutation.isPending || deleteLineMutation.isPending}
          error={deleteError}
          showExternalStorageWarning={deleteTarget.type === "invoice" && deletesExternalOnInvoiceDelete}
          onCancel={() => setDeleteTarget(null)}
          onDelete={handleDeleteConfirmed}
        />
      )}
      {exportModalOpen && (
        <StorageExportModal invoiceId={inv.id} businessId={businessId} onClose={() => setExportModalOpen(false)} />
      )}

      <div className="space-y-4">
        {/* Original file row */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-2.5"
          style={{ backgroundColor: "white", border: `1px solid ${NAVY}12` }}>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.45 }}>Original invoice file</p>
            <p className="text-sm mt-1 truncate" style={{ color: NAVY }}>{inv.pdfFileName || "Stored private file"}</p>
            {externalStorageNotice && <p className="text-xs mt-1" style={{ color: "#b91c1c" }}>{externalStorageNotice}</p>}
          </div>
          <div className="relative flex flex-wrap items-center gap-2">
            {inv.status === "duplicate" ? (
              <>
                <button type="button" disabled={resolveDuplicateMutation.isPending}
                  onClick={() => resolveDuplicateMutation.mutate("overwrite")}
                  className="rounded-md px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50"
                  style={{ color: ORANGE, backgroundColor: `${ORANGE}0d`, border: `1px solid ${ORANGE}44` }}>
                  Overwrite original
                </button>
                <button type="button" disabled={resolveDuplicateMutation.isPending}
                  onClick={() => resolveDuplicateMutation.mutate("delete")}
                  className="w-8 h-8 rounded-md flex items-center justify-center transition-all disabled:opacity-50"
                  style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.16)" }}>
                  <Trash2 size={13} />
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={openExternalStorage} disabled={externalLoading || externalStorageUnavailable}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition-all disabled:opacity-50"
                  style={{ backgroundColor: "white", color: NAVY, border: `1.5px solid ${NAVY}22` }}>
                  {externalLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                  {externalLoading ? "Loading..." : "View in external storage"}
                  {(originalFile?.externalStorage?.length || 0) > 1 && <ChevronDown size={12} />}
                </button>
                {externalMenuOpen && originalFile?.externalStorage && originalFile.externalStorage.length > 1 && (
                  <div className="absolute right-0 top-full z-20 mt-2 w-72 overflow-hidden rounded-lg bg-white shadow-lg"
                    style={{ border: `1px solid ${NAVY}18` }}
                    onClick={(e) => e.stopPropagation()}>
                    {originalFile.externalStorage.map((dest, idx) => (
                      <a key={`${dest.provider}-${idx}`} href={dest.url || undefined} target="_blank" rel="noreferrer"
                        onClick={(e) => { if (!dest.url) e.preventDefault(); setExternalMenuOpen(false); }}
                        className="block px-3 py-2.5 transition-colors"
                        style={{ borderTop: idx > 0 ? `1px solid ${NAVY}0d` : undefined, color: NAVY }}>
                        <span className="block text-xs font-bold capitalize">{dest.provider}</span>
                        <span className="block truncate text-[11px] opacity-60">{dest.path}</span>
                      </a>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => setExportModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all"
                  style={{ backgroundColor: "white", color: "#2563eb", border: "1.5px solid rgba(37,99,235,0.22)" }}>
                  <CloudUpload size={13} />
                  Upload to cloud
                </button>
                <button type="button" onClick={openOriginalFile} disabled={previewLoading}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: ORANGE, boxShadow: "0 2px 8px rgba(96,120,64,0.25)" }}>
                  {previewLoading ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                  {previewLoading ? "Loading..." : "Preview original"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Alarms */}
        {criticalAlarms.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">Alarms</p>
            {criticalAlarms.map((alarm) => (
              <div key={alarm.id} className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-red-500" />
                {alarm.message}
              </div>
            ))}
          </div>
        )}
        {warningAlarms.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-yellow-700">Warnings</p>
            {warningAlarms.map((alarm) => (
              <div key={alarm.id} className="flex items-start gap-2.5 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-yellow-500" />
                {alarm.message}
              </div>
            ))}
          </div>
        )}

        {/* Details + line items */}
        <div className={lineItemsOnly ? undefined : "grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start"}>
          {/* Invoice details — hidden in line-items-only mode */}
          {!lineItemsOnly && <div className="rounded-lg p-3"
            style={{
              border: isFlagged ? "1px solid rgba(220,38,38,0.18)" : hasWarnings ? "1px solid rgba(202,138,4,0.22)" : `1px solid ${NAVY}12`,
              backgroundColor: isFlagged ? "rgba(254,242,242,0.7)" : hasWarnings ? "rgba(254,252,232,0.8)" : "white",
            }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3"
              style={{ color: isFlagged ? "#dc2626" : hasWarnings ? "#a16207" : NAVY, opacity: isFlagged || hasWarnings ? 1 : 0.45 }}>
              Invoice details
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
                style={{ border: `1px solid ${NAVY}12`, backgroundColor: "rgba(255,255,255,0.72)" }}>
                <span className="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: NAVY, opacity: 0.5 }}>Number</span>
                <div className="w-32 min-w-0">
                  <EditableCell value={inv.invoiceNumber} onSave={(v) => invoiceMutation.mutate({ invoiceNumber: v })} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
                style={{ border: `1px solid ${NAVY}12`, backgroundColor: "rgba(255,255,255,0.72)" }}>
                <span className="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: NAVY, opacity: 0.5 }}>Date</span>
                <div className="w-32 min-w-0">
                  <EditableCell type="date" value={inv.invoiceDate} onSave={(v) => invoiceMutation.mutate({ invoiceDate: v })} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
                style={{ border: `1px solid ${NAVY}12`, backgroundColor: "rgba(255,255,255,0.72)" }}>
                <span className="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: NAVY, opacity: 0.5 }}>Subtotal</span>
                <span className="text-xs font-semibold truncate" style={{ color: NAVY }}>{displayCurrency(inv.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
                style={{ border: `1px solid ${NAVY}12`, backgroundColor: "rgba(255,255,255,0.72)" }}>
                <span className="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: NAVY, opacity: 0.5 }}>GST</span>
                <span className="text-xs font-semibold truncate" style={{ color: NAVY }}>{displayCurrency(inv.taxAmount)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
                style={{ border: `1px solid ${NAVY}18`, backgroundColor: `${NAVY}05` }}>
                <span className="text-[10px] font-bold uppercase tracking-wide whitespace-nowrap" style={{ color: NAVY, opacity: 0.58 }}>Total</span>
                <div className="w-32 min-w-0">
                  <EditableCell type="number" align="right" value={inv.totalAmount} onSave={(v) => invoiceMutation.mutate({ totalAmount: v })} />
                </div>
              </div>
            </div>

            {!inv.invoiceDate && suggestedDate && (
              <div className="mt-2">
                <button type="button" onClick={() => invoiceMutation.mutate({ invoiceDate: suggestedDate })}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-xs font-semibold"
                  style={{ border: `1px solid ${ORANGE}55`, color: ORANGE, backgroundColor: `${ORANGE}0d` }}>
                  <span>Use upload date</span>
                  <span className="font-bold">{suggestedDate}</span>
                </button>
              </div>
            )}

            <div className="mt-2">
              <button type="button" onClick={() => invoiceMutation.mutate({ totalAmount: formatNumber(suggestedTotal) })}
                className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-xs font-semibold"
                style={{ border: `1px solid ${ORANGE}55`, color: ORANGE, backgroundColor: `${ORANGE}0d` }}>
                <span>Use line total</span>
                <span className="font-bold">{displayCurrency(suggestedTotal)}</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <button type="button" onClick={() => recheckMutation.mutate()}
                disabled={recheckMutation.isPending || invoiceMutation.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
                style={{ backgroundColor: ORANGE, boxShadow: "0 2px 8px rgba(96,120,64,0.25)" }}>
                <CheckCircle size={13} />
                {recheckMutation.isPending ? "Checking..." : invoiceMutation.isPending ? "Saving…" : "Finish"}
              </button>
              <button type="button"
                onClick={() => confirmDelete({ type: "invoice", id: inv.id, title: "Delete invoice?", body: `Delete invoice ${inv.invoiceNumber || "without a number"} and all its line items.` })}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all"
                style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.16)" }}>
                <Trash2 size={13} />
                Delete
              </button>
            </div>
            {recheckMessage && (
              <div className="mt-3 rounded-md px-3 py-2 text-xs font-medium"
                style={{
                  color: recheckMessage.includes("Warnings:") ? "#a16207" : recheckMessage.startsWith("Invoice") ? "#047857" : "#b91c1c",
                  backgroundColor: recheckMessage.includes("Warnings:") ? "rgba(202,138,4,0.08)" : recheckMessage.startsWith("Invoice") ? "rgba(4,120,87,0.08)" : "rgba(220,38,38,0.06)",
                }}>
                  {recheckMessage}
              </div>
            )}
          </div>}

          {/* Line items */}
          <div className="min-w-0 rounded-lg p-3" style={{ border: `1px solid ${NAVY}12`, backgroundColor: "white" }}>
            {inv.lineItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.45 }}>
                    Line Items — edit inline
                  </p>
                  <button type="button" onClick={() => { setLineCreateError(""); setAddingLine(true); }}
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold text-white"
                    style={{ backgroundColor: ORANGE, boxShadow: "0 2px 8px rgba(96,120,64,0.2)" }}>
                    <Plus size={12} />
                    Add line
                  </button>
                </div>
                <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid rgba(80,55,30,0.1)", backgroundColor: "white" }}>
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(80,55,30,0.08)", backgroundColor: "rgba(80,55,30,0.03)" }}>
                        <th className="pl-4 pr-2 py-2.5 text-xs font-semibold text-right w-8" style={{ color: NAVY, opacity: 0.35 }}>#</th>
                        {["Product Code", "Description", "Qty", "Unit", "Unit Price", "Total", ...(lineItemsOnly ? [] : [""])].map((h, i) => (
                          <th key={h || "actions"} className={`px-4 py-2.5 text-xs font-semibold ${i < 2 ? "text-left" : "text-right"}`} style={{ color: NAVY, opacity: 0.5 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inv.lineItems.map((item, idx) => {
                        const isHighlighted = Boolean(highlightProductCode && item.productCode && item.productCode === highlightProductCode);
                        return (
                        <tr key={item.id} style={{ borderTop: "1px solid rgba(80,55,30,0.06)", backgroundColor: isHighlighted ? "rgba(234,179,8,0.12)" : undefined, outline: isHighlighted ? "2px solid rgba(234,179,8,0.5)" : undefined, outlineOffset: isHighlighted ? "-2px" : undefined }}>
                          <td className="pl-4 pr-2 py-2.5 text-right text-xs font-mono select-none" style={{ color: NAVY, opacity: 0.3 }}>{idx + 1}</td>
                          <td className="px-4 py-2.5 w-[120px]"><EditableCell value={item.productCode || ""} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { productCode: v } })} /></td>
                          <td className="px-4 py-2.5 w-[34%]"><EditableCell value={item.description} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { description: v } })} /></td>
                          <td className="px-4 py-2.5 text-right"><EditableCell type="number" align="right" value={item.quantity} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { quantity: v } })} /></td>
                          <td className="px-4 py-2.5 text-right"><EditableCell align="right" value={item.unit} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { unit: v } })} /></td>
                          <td className="px-4 py-2.5 text-right"><EditableCell type="number" align="right" value={item.unitPrice} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { unitPrice: v } })} /></td>
                          <td className="px-4 py-2.5 text-right font-bold"><EditableCell type="number" align="right" value={item.totalPrice} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { totalPrice: v } })} /></td>
                          {!lineItemsOnly && (
                            <td className="px-4 py-2.5 text-right">
                              <button type="button"
                                onClick={() => confirmDelete({ type: "line", id: item.id, title: "Delete invoice line?", body: `Delete ${item.description || "this line item"} from this invoice.` })}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-all"
                                style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.16)" }}>
                                <Trash2 size={13} />
                              </button>
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {inv.lineItems.length === 0 && (
              <button type="button" onClick={() => { setLineCreateError(""); setAddingLine(true); }}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold text-white"
                style={{ backgroundColor: ORANGE, boxShadow: "0 2px 8px rgba(96,120,64,0.2)" }}>
                <Plus size={12} />
                Add invoice line
              </button>
            )}

            {addingLine && (
              <AddLineForm
                defaultCategory={inv.supplier.category}
                isSaving={createLineMutation.isPending}
                categories={categories}
                onCancel={() => { setAddingLine(false); setLineCreateError(""); }}
                onSubmit={handleAddLine}
              />
            )}
            {lineCreateError && (
              <p className="text-xs rounded-md px-3 py-2 mt-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>{lineCreateError}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
